import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Input, Modal, ModalProps, useModals } from "@skailer-com/uikit";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface SaveViewModalProps extends ModalProps {
  onSubmitView: (title: string) => Promise<void>;
  initialTitle?: string;
  heading?: string;
  submitLabel?: string;
}

export function SaveViewModal({
  onSubmitView,
  initialTitle,
  heading,
  submitLabel,
  ...props
}: SaveViewModalProps) {
  const { close } = useModals();
  const { id } = props;
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);

  const schema = z.object({
    title: z
      .string({ error: t("errors:field_required") })
      .trim()
      .min(1, t("errors:field_required")),
  });
  type Schema = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Schema>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    defaultValues: { title: initialTitle ?? "" },
  });

  const onSubmit = handleSubmit(async (data) => {
    setIsSaving(true);
    try {
      await onSubmitView(data.title.trim());
      close(id);
    } catch {
      setIsSaving(false);
    }
  });

  return (
    <Modal
      {...props}
      variant="middle"
      title={heading ?? t("core:save_new_view")}
      withCloser
      closeByEscEnabled
      actions={
        <Button type="button" size="lg" onClick={onSubmit} isLoading={isSaving} className="w-full">
          {submitLabel ?? t("core:save_view")}
        </Button>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          label={t("core:view_name")}
          required
          autoFocus
          type="text"
          {...register("title")}
          error={errors.title?.message}
        />
      </form>
    </Modal>
  );
}
